/**
 * Backbone model class which provides a flexible way to retrieve, update, and display attributes for a given object.
 *
 * # Fields
 * The default field values are described below. Any function supplied as a field value will be scoped to the field model.
 * ## name
 * Type | Default
 * --- | ---
 * `String` | `''`
 *
 * The name of the field to which this model pertains. This also acts as the primary key for this model.
 * ## editValue
 * Type | Default
 * --- | ---
 * `String` &#124; `Function` | Takes the value of `name` by default.
 *
 * The actual object attribute to use to retrieve and set the value to use in the field editor. This can be a `function(model)`, where the `model` argument is the model from which to derive the value.
 * ## viewValue
 * Type | Default
 * --- | ---
 * `String` &#124; `Function` | Takes the value of `editValue` by default.
 *
 * The actual object attribute to use to retrieve and set the value to use in the field viewer. This can be a `function(model)`, where the `model` argument is the model from which to derive the value.
 *
 * ## label
 * Type | Default
 * --- | ---
 * `String` | `''`
 *
 * The field label.
 *
 * ## visible
 * Type | Default
 * --- | ---
 * `Boolean` &#124; `Function` | `false`
 *
 * A value indicating whether or not this field should be visible *OR* a `function(model)` which, given the model in question, returns a boolean indicating whether or not the field should be visible for that model.
 * ## editable
 * Type | Default
 * --- | ---
 * `Boolean` &#124; `Function` | `false`, unless an `editor` or `values` attribute is supplied, in which case this defaults to `true`.
 *
 * A value indicating whether or not this field should be editable *OR* a `function(model)` which, given the model in question, returns a boolean indicating whether or not the field should be editable for that model.
 * ## editor
 * Type | Default
 * --- | ---
 * `String` &#124; `Function` | `"text"`. If `editable` is truthy and `type` is specified, but this is not, the appropriate value will be derived from `type`.
 *
 * Determines the type of editor control to use for the field. Any HTML input type (including "textarea") is valid, in addition to "color" and "button". This can also be a `function(value, model, updateCallback)`, where `value` is the current `editValue` of the model in question, `model` is the model in question, and `updateCallback` is a `function()` to be called whenever the value in the editor changes.
 *
 * ## values
 * Type | Default
 * --- | ---
 * `Array` &#124; `String` | `null`
 *
 * For enumerated editor types - "select", "radio", and "button" - provides a way to supply the values of the options. Each array element should have a `value` key, specifying the actual attribute value associated with the option, and a `text` key, containing the text to display for the option. A Lookup model type may also be supplied here, in which case `App.lookups` will be used to determine the list of values.
 * ## sortable
 * Type | Default
 * --- | ---
 * `Boolean` &#124; `String` &#124; `Function` | `false`
 *
 * Specifies whether or not the attribute should be "sortable". If `true`, the `editValue` of each object will be supplied for sorting. If a non-empty string is provided, that attribute of each object will be supplied for sorting. Finally, if a `function(model)` is supplied, the return value of that function for each object will be supplied as the sort value.
 * ## filterable
 * Type | Default
 * --- | ---
 * `Boolean` &#124; `String` &#124; `Function` | `false`
 *
 * Specifies whether or not the attribute should be "filterable". If `true`, the `editValue` of each object will be supplied for filtering. If a non-empty string is provided, that attribute of each object will be supplied for filtering. Finally, if a `function(model)` is supplied, the return value of that function for each object will be supplied as the filter value.
 *
 * ## type
 * **Type:** `String`
 * ### Default Value
 * Derives a value from `editor` if `editor` is not a function. If `editor` is a function, the default value is `custom`. If `editor` is not specified, the default value is `Enums.FormQuestionType.Text`.
 * ### Description
 * The actual type of data stored in the field. For long text, use `Enums.FormQuestionType.ExtendedText`.
 *
 * ## valueSource
 * **Type:** `String`
 * ### Default Value
 * None
 * ### Description
 * A name of a Backbone.Collection module. Specifies that members of that collection shall be presented in a selection field. The values will load asynchronously, and an appropriate message will be displayed while loading is occuring.
 *
 * ## viewOptions
 * **Type:** `Object`
 * ### Default Value
 * `{}`
 * ### Description
 * Any additional options to pass to views created which display the field. Should **not** contain any HTML markup language.
 * @module app/Field
 * @extend Backbone.Model
 */

import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import FieldTypes from "core/models/FieldTypes";
import s from "underscore.string";
/**
 * Gets a value indicating whether or not fields of the given type have a set of values associated with them.
 * @param {String} enumValue The key of `FieldTypes` to check.
 * @return {Boolean} True if the field is enumerated, otherwise false.
 */
function hasValues(enumValue) {
  return _.contains(
    [FieldTypes.Select, FieldTypes.Radio, FieldTypes.Check, FieldTypes.Button],
    enumValue
  ); //select, radio, check, buttons
}

/**
 * Gets a value indicating whether or not this field is a "static" field type - one that doesn't involve data entry.
 * @param {String} enumValue The key of `FieldTypes` to check.
 * @return {Boolean} True if static, otherwise false.
 */
function isStatic(enumValue) {
  return _.contains([FieldTypes.Custom], enumValue);
}

/**
 * Gets a value indicating whether or not values of this field are simple values, as opposed to other objects.
 * @param {String} enumValue The key of `FieldTypes` to check.
 * @return {Boolean} True if simple, otherwise false.
 */
function isSimple(enumValue) {
  return !isStatic(enumValue);
}

/**
 * Gets a value indicating whether or not values of this field are sortable.
 * @param {String} enumValue The key of `Enums.FormQuestionType` to check.
 * @return {Boolean} True if sortable, otherwise false.
 */
function isSortable(enumValue) {
  return (
    isSimple(enumValue) &&
    !_.contains([FieldTypes.Check, FieldTypes.CheckBox], enumValue)
  );
}

/**
 * Gets a value indicating whether or not values of this field are filterable.
 * @param {String} enumValue The key of `Enums.FormQuestionType` to check.
 * @return {Boolean} True if filterable, otherwise false.
 */
function isFilterable(enumValue) {
  //text, select, radio, numeric, buttons
  return _.contains(
    [
      FieldTypes.Text,
      FieldTypes.Select,
      FieldTypes.Radio,
      FieldTypes.Number,
      FieldTypes.Button,
    ],
    enumValue
  );
}

var Field = Backbone.Model.extend(
  {
    defaults: {
      editValue: "",
      viewValue: "",
      label: "",
      editable: false,
      editor: FieldTypes.Text,
      type: FieldTypes.Text,
      sortable: false,
      filterable: false,
      values: null,
      valueSource: null,
      visible: true,
      validators: [],
      viewOptions: {},
    },

    /**
     * The constructor for this class.
     * @param  {object} attributes The list of attribute values. See class description for usage.
     * @param  {object} options    A mapping of options. Not used.
     */
    initialize: function (attributes, options) {
      var attrs = this.attributes;
      var type = attributes.editor;

      if (!attributes.editValue) {
        attrs.editValue = attributes.name;
      }

      if (!attributes.viewValue) {
        attrs.viewValue = attrs.editValue;
      }

      if (attributes.editor || attributes.values || attributes.valueSource) {
        attrs.editable = attributes.editable !== false;

        if (attributes.values || attributes.valueSource) {
          type = attributes.editor || FieldTypes.Select;
        }
      } else if (attributes.editable) {
        type = attributes.type;
      }

      attrs.type = type;
      attrs.editor = type;

      if (_.isUndefined(attributes.filterable) && isFilterable(attrs.type)) {
        attrs.filterable = true;
      }

      if (_.isUndefined(attributes.sortable) && isSortable(attrs.type)) {
        attrs.sortable = true;
      }

      if (_.isUndefined(attributes.listable) && isSimple(attrs.type)) {
        attrs.listable = true;
      }

      this._originalAttributes = _.clone(attrs);
      this._originalOptions = _.clone(options);

      _.each(
        attrs,
        function (attr, key) {
          if (_.isFunction(attr)) {
            attrs[key] = _.bind(attr, this);
          }
        },
        this
      );

      if (options) {
        if (_.isFunction(options.getter)) {
          this.getValue = _.bind(options.getter, this);
        }

        if (_.isFunction(options.setter)) {
          this.setValue = _.bind(options.setter, this);
        }
      }

      this.on(
        "change:label",
        function () {
          if (!this.get("label") && options && options.fieldSet) {
            options.fieldSet.removeField(this.id || this.cid);
          }
        },
        this
      );

      this.on(
        "change:type",
        function () {
          var type = this.get("type");

          if (hasValues(type)) {
            this.set("values", this.get("values") || []);
          } else {
            this.set("values", null);
          }
        },
        this
      );
    },

    /**
     * Creates a new Field which "wraps" this Field. Access to this Field is available in the new Field via the `wrapper` property.
     * @param  {object} attributes A map of attributes which shall override the original attributes in the new Field instance.
     * @param {object} options A map of options which shall override the original options in the new Field instance.
     * @return {Field}            The Field instance.
     */
    decorate: function (attributes, options) {
      var field = new Field(
        _.defaults({}, attributes, this._originalAttributes),
        _.defaults({}, options, this._originalOptions)
      );

      field.wrapper = this;

      return field;
    },

    /**
     * Gets a value indicating whether or not this field should be visible for the given model.
     * @param  {object}  model The model in question.
     * @return {Boolean}       True if the field should be visible, otherwise false.
     */
    isVisible: function (model) {
      if (_.isFunction(this.attributes.visible)) {
        return this.attributes.visible(model);
      } else {
        return this.attributes.visible;
      }
    },

    /**
     * Gets a value indicating whether or not this field should be editable for the given model.
     * @param  {object}  model The model in question.
     * @return {Boolean}       True if the field should be editable, otherwise false.
     */
    isEditable: function (model) {
      if (_.isFunction(this.attributes.editable)) {
        return this.attributes.editable(model);
      } else {
        return this.attributes.editable;
      }
    },

    _getFieldValue: function (model, attribute) {
      return Field.getValue(model, attribute);
    },

    /**
     * Gets the value to use for sorting the given model by this field.
     * @param  {object} model The model in question.
     * @return The sort value, or `undefined`, if sorting is not allowed.
     */
    getSortValue: function (model) {
      var value;
      var field = this.attributes;

      if (field.sortable === true) {
        value = this.getValue(model);
      } else if (_.isString(field.sortable)) {
        value = this._getFieldValue(model, field.sortable);
      } else if (_.isFunction(field.sortable)) {
        value = field.sortable(model);
      }

      return value;
    },

    /**
     * Gets the value of the model property with name matching this instance's `name` attribute.
     * @param  {object} model The model in question.
     * @return The value of the property.
     */
    getValue: function (model) {
      return Field.getValue(model, this);
    },

    /**
     * Sets the value of the model property with name matching this instance's `name` attribute.
     * @param  {object} model The model in question.
     * @param value The new value for the property.
     */
    setValue: function (model, value) {
      Field.setValue(model, this, value);
    },

    /**
     * Gets the `editValue` of the model, or the value to use in controls which allow for editing of model values.
     * @param  {object} model The model in question.
     * @return The `editValue`.
     */
    getEditValue: function (model) {
      return this._getFieldValue(model, this.attributes.editValue);
    },

    /**
     * Gets the `viewValue` of the model, or the value to display in controls which display model values.
     * @param  {object} model The model in question.
     * @return The `viewValue`.
     */
    getViewValue: function (model) {
      return this._getFieldValue(model, this.attributes.viewValue);
    },

    /**
     * Sets the `editValue` of the model.
     * @param {object} model The model in question.
     * @param value The `editValue` to set.
     */
    setFieldValue: function (model, value) {
      Field.setValue(model, this.attributes.editValue, value);
    },

    /**
     * Gets the value to use for filtering the given model by this field.
     * @param  {object} model The model in question.
     * @return {mixed}       The filter value, or `undefined`, if filtering is not allowed.
     */
    getFilterValue: function (model) {
      var value;
      var field = this.attributes;

      if (field.filterable === true) {
        value = this.getValue(model);
      } else if (_.isString(field.filterable)) {
        value = this._getFieldValue(model, field.filterable);
      } else if (_.isFunction(field.filterable)) {
        value = field.filterable(model);
      }

      return value;
    },

    /**
     * Adds a value to the set of potential values for this Field. Only applicable for list-type fields.
     * @param {String} text The display text of the value.
     * @param {String} [id] The identifier of the value.
     * @return {Object} The value that was added, if any.
     */
    addListValue: function (text, id) {
      var values = this.get("values");
      var value;

      if (_.isArray(values) && text) {
        var uniqueId = this.id
          ? _.uniqueId(this.id + this.cid)
          : _.uniqueId(this.cid);

        value = {
          value: id || uniqueId,
          text: text,
        };
        values.push(value);

        this.set("values", values);
        this.trigger("value-added", value.value, value);
      }

      return value;
    },

    /**
     * Removes the value with the specified key from the set of potential values. Only applicable for list-type fields.
     * @param {String} key The identifier of the value.
     */
    removeListValue: function (key) {
      var values = this.get("values");
      var value;

      if (_.isArray(values)) {
        value = _.find(values, function (value) {
          return value.value == key;
        });
        values = _.reject(values, function (value) {
          return value.value == key;
        });

        if (value) {
          this.set("values", values);
          this.trigger("value-removed", key, value);
        }
      }
    },

    /**
     * Updates the text of the value with the given key. Only applicable for list-type fields. If no key is given, or if the key is falsy, the text value is added to the list.
     * @param {String} [key] The identifier of the value.
     * @param {String} text The display text of the value.
     * @return {object} The value that was added or updated.
     */
    setListValue: function (key, text) {
      var values, value;

      if (!_.isString(text)) {
        text = key;
        key = null;
      }

      if (this.hasValues()) {
        if (!key) {
          value = this.addListValue(text);
        } else {
          values = this.get("values");
          value = _.find(values, function (value) {
            return value.value == key;
          });

          if (value) {
            if (text) {
              value.text = text;
              this.trigger("value-updated", value.value, value);
            } else {
              this.removeListValue(value.value);
            }
          }
        }
      }

      return value;
    },

    addValueRule: function (rule, id) {
      var values = this.get("values");
      var value = _.find(values, function (value) {
        return value.value == id;
      });
      if (value == undefined) {
        return false;
      }

      var rules = value.rules;
      if (!_.isArray(rules)) {
        rules = [];
      }
      if (rule) {
        rules.push(rule);
      }
      value.rules = rules;
      this.trigger("value-rule-added", value, rule);
      return rules;
    },

    removeValueRule: function (rule, id) {
      var values = this.get("values");
      var value = _.find(values, function (value) {
        return value.value == id;
      });
      if (value == undefined) {
        return false;
      }

      var rules = value.rules;
      if (!_.isArray(rules)) {
        rules = [];
      }
      if (rule) {
        rules.splice(rules.indexOf(rule), 1);
      }
      value.rules = rules;
      this.trigger("value-rule-removed", value, rule);
      return rules;
    },

    clearRules: function (id) {
      var values = this.get("values");
      var value = _.find(values, function (value) {
        return value.value == id;
      });
      if (value == undefined) {
        return false;
      }
      value.rules = [];
      this.trigger("value-rule-cleared", id);
      return value;
    },

    /**
     * Gets a value indicating whether or not this field has discrete values.
     * @return {Boolean} True if there are values, otherwise false.
     */
    hasValues: function () {
      return _.isArray(this.get("values"));
    },

    /**
     * Gets a value indicating whether or not this field can be filtered.
     * @return {boolean} True if the field can be filtered, otherwise false.
     */
    isFilterable: function () {
      return !!this.get("filterable");
    },

    /**
     * Gets a value indicating whether or not this field can be sorted.
     * @return {boolean} True if the field can be sorted, otherwise false.
     */
    isSortable: function () {
      return !!this.get("sortable");
    },

    /**
     * Gets a value indicating whether or not this field should be shown in a listing.
     * @return {boolean} True if the field is listable, otherwise false.
     */
    isListable: function () {
      return !!this.get("listable");
    },

    toJSON: function () {
      var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

      json = _.omit(json, _.functions(json));
      json = _.omit(json, ["editor", "editValue", "viewValue"]);

      return json;
    },
  },
  {
    fields: {
      //so meta
      visible: {
        name: "visible",
        label: "Visible",
        editor: "check",
      },
      label: {
        name: "label",
        label: "Field Label",
        editor: "text",
        visible: function (model) {
          return !isStatic(model.get("type"));
        },
      },
      category: {
        name: "category",
        label: "Tab",
        editor: "text",
      },
      filterable: {
        name: "filterable",
        label: "Filterable",
        editor: "check",
      },
      sortable: {
        name: "sortable",
        label: "Sortable",
        editor: "check",
      },
      listable: {
        name: "listable",
        label: "Listable",
        editor: "check",
      },
      type: {
        name: "type",
        label: "Field Type",
        values: _.map(FieldTypes, function (value, text) {
          return {
            value: value,
            text: s.humanize(text),
          };
        }),
      },
      description: {
        name: "description",
        label: "Field Description (optional)",
        editor: "text",
        visible: function (model) {
          return isStatic(model.get("type"));
        },
      },
    },

    /**
     * Gets the value of the given field in the given model. If the field specified is not a key of the model attributes, `_.result()` is called, passing the model and the field, and the result is returned.
     * @param {Backbone.Model} model The model containing the field to evaluate.
     * @param {String|Field} field Either a field instance or a string. If a field instance is given, the `name` attribute is used for further processing.
     * @static
     * @return The value of the field.
     */
    getValue: function (model, field) {
      field = _.isObject(field) ? field.id || field.get("name") : field;

      return model.get(field) || _.result(model, field);
    },

    /**
     * Sets the value of the given field in the given model. The field specified will become a key in the model attributes if it does not already exist.
     * @param {Backbone.Model} model The model containing the field to set.
     * @param {String|Field} field Either a field instance or a string. If a field instance is given, the `name` attribute is used for further processing.
     * @param value The value to set in the field.
     * @static
     */
    setValue: function (model, field, value) {
      field = _.isObject(field) ? field.id || field.get("name") : field;

      model.set(field, value);
    },
  }
);

export default Field;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/models/Field.js
// module id = 11
// module chunks = 1